/**
 * App root — React Router với 5 route Phase 1:
 *   /login                — đăng ký/login dev (2D).
 *   /onboarding/intro     — story 15s (2D).
 *   /onboarding/name      — đặt tên quán (2D).
 *   /                     — MainCafeScene (3D Three.js).
 *   /shop2d               — ShopScreen 2D fallback (giữ phòng thiết bị yếu).
 *
 * Auth guard: nếu chưa có access token → redirect /login khi nhận 401.
 */
import { useEffect } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { MilestoneToast } from "./components/MilestoneToast";
import { TelegramBootstrap } from "./components/TelegramBootstrap";
import { getAccessToken, onUnauthorized } from "./lib/api";
import { preloadSounds } from "./lib/sound";
import { MainCafeScene } from "./scenes/MainCafe/MainCafeScene";
import { IntroScreen } from "./screens/IntroScreen";
import {
  hasChosenLocale,
  LanguageSelector,
} from "./screens/LanguageSelector";
import { LoginScreen } from "./screens/LoginScreen";
import { QuestScreen } from "./screens/QuestScreen";
import { ShopNameScreen } from "./screens/ShopNameScreen";
import { ShopScreen } from "./screens/ShopScreen";
import { StatsScreen } from "./screens/StatsScreen";
import { UpgradeScreen } from "./screens/UpgradeScreen";
import { useShopStore } from "./stores/shopStore";

function RequireLanguage({ children }: { children: React.ReactNode }) {
  // Lần đầu vào app: chưa chọn ngôn ngữ → đẩy /language.
  // Khi user đã chọn xong (hoặc đến trực tiếp /language), cho qua.
  const location = useLocation();
  if (!hasChosenLocale() && location.pathname !== "/language") {
    return <Navigate to="/language" replace />;
  }
  return <>{children}</>;
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = getAccessToken();
  const location = useLocation();
  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}

function RedirectIfAuth({ children }: { children: React.ReactNode }) {
  // Token persist localStorage → reload tab vẫn còn. Nếu user lạc vào /login
  // (bookmark cũ, tab cũ) thì đẩy thẳng về / thay vì bắt login lại.
  const token = getAccessToken();
  if (token) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function AppShell() {
  const navigate = useNavigate();
  const logout = useShopStore((s) => s.logout);

  useEffect(() => {
    preloadSounds();
    const unsubscribe = onUnauthorized(() => {
      logout();
      navigate("/login");
    });
    return unsubscribe;
  }, [logout, navigate]);

  return (
    <>
      <Routes>
        <Route path="/language" element={<LanguageSelector />} />
        <Route
          path="/login"
          element={
            <RequireLanguage>
              <RedirectIfAuth>
                <LoginScreen />
              </RedirectIfAuth>
            </RequireLanguage>
          }
        />
        <Route
          path="/onboarding/intro"
          element={
            <RequireAuth>
              <IntroScreen />
            </RequireAuth>
          }
        />
        <Route
          path="/onboarding/name"
          element={
            <RequireAuth>
              <ShopNameScreen />
            </RequireAuth>
          }
        />
        <Route
          path="/"
          element={
            <RequireAuth>
              <MainCafeScene />
            </RequireAuth>
          }
        />
        <Route
          path="/upgrade"
          element={
            <RequireAuth>
              <UpgradeScreen />
            </RequireAuth>
          }
        />
        <Route
          path="/quest"
          element={
            <RequireAuth>
              <QuestScreen />
            </RequireAuth>
          }
        />
        <Route
          path="/stats"
          element={
            <RequireAuth>
              <StatsScreen />
            </RequireAuth>
          }
        />
        <Route
          path="/shop2d"
          element={
            <RequireAuth>
              <ShopScreen />
            </RequireAuth>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {/* Telegram Mini App bootstrap — auto-auth + theme khi mở qua Telegram */}
      <TelegramBootstrap />
      {/* Global overlays */}
      <MilestoneToast />
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}
